import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const GoogleTranslate = () => {
    const [selectedLanguage, setSelectedLanguage] = useState({ code: 'en', name: 'English', flag: 'gb' });
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const languages = [
        { code: 'en', name: 'English', flag: 'gb' },
        { code: 'sq', name: 'Albanian', flag: 'al' },
        { code: 'ar', name: 'Arabic', flag: 'sa' },
        { code: 'bn', name: 'Bengali', flag: 'bd' },
        { code: 'bg', name: 'Bulgarian', flag: 'bg' },
        { code: 'ca', name: 'Catalan', flag: 'es-ct' }, // Ad-hoc flag or es
        { code: 'zh-CN', name: 'Chinese (Simplified)', flag: 'cn' },
        { code: 'zh-TW', name: 'Chinese (Traditional)', flag: 'tw' },
        { code: 'hr', name: 'Croatian', flag: 'hr' },
        { code: 'cs', name: 'Czech', flag: 'cz' },
        { code: 'da', name: 'Danish', flag: 'dk' },
        { code: 'nl', name: 'Dutch', flag: 'nl' },
        { code: 'et', name: 'Estonian', flag: 'ee' },
        { code: 'tl', name: 'Filipino', flag: 'ph' },
        { code: 'fi', name: 'Finnish', flag: 'fi' },
        { code: 'fr', name: 'French', flag: 'fr' },
        { code: 'gl', name: 'Galician', flag: 'es-ga' }, // Ad-hoc
        { code: 'de', name: 'German', flag: 'de' },
        { code: 'el', name: 'Greek', flag: 'gr' },
        { code: 'gu', name: 'Gujarati', flag: 'in' },
        { code: 'iw', name: 'Hebrew', flag: 'il' },
        { code: 'hi', name: 'Hindi', flag: 'in' },
        { code: 'hu', name: 'Hungarian', flag: 'hu' },
        { code: 'id', name: 'Indonesian', flag: 'id' },
        { code: 'it', name: 'Italian', flag: 'it' },
        { code: 'ja', name: 'Japanese', flag: 'jp' },
        { code: 'kn', name: 'Kannada', flag: 'in' },
        { code: 'ko', name: 'Korean', flag: 'kr' },
        { code: 'lv', name: 'Latvian', flag: 'lv' },
        { code: 'lt', name: 'Lithuanian', flag: 'lt' },
        { code: 'ms', name: 'Malay', flag: 'my' },
        { code: 'ml', name: 'Malayalam', flag: 'in' },
        { code: 'mt', name: 'Maltese', flag: 'mt' },
        { code: 'mr', name: 'Marathi', flag: 'in' },
        { code: 'ne', name: 'Nepali', flag: 'np' },
        { code: 'no', name: 'Norwegian', flag: 'no' },
        { code: 'or', name: 'Odia', flag: 'in' },
        { code: 'fa', name: 'Persian', flag: 'ir' },
        { code: 'pl', name: 'Polish', flag: 'pl' },
        { code: 'pt', name: 'Portuguese', flag: 'pt' },
        { code: 'pa', name: 'Punjabi', flag: 'in' },
        { code: 'ro', name: 'Romanian', flag: 'ro' },
        { code: 'ru', name: 'Russian', flag: 'ru' },
        { code: 'sr', name: 'Serbian', flag: 'rs' },
        { code: 'sk', name: 'Slovak', flag: 'sk' },
        { code: 'sl', name: 'Slovenian', flag: 'si' },
        { code: 'es', name: 'Spanish', flag: 'es' },
        { code: 'sv', name: 'Swedish', flag: 'se' },
        { code: 'ta', name: 'Tamil', flag: 'in' },
        { code: 'te', name: 'Telugu', flag: 'in' },
        { code: 'th', name: 'Thai', flag: 'th' },
        { code: 'tr', name: 'Turkish', flag: 'tr' },
        { code: 'uk', name: 'Ukrainian', flag: 'ua' },
        { code: 'ur', name: 'Urdu', flag: 'pk' },
        { code: 'uz', name: 'Uzbek', flag: 'uz' },
        { code: 'vi', name: 'Vietnamese', flag: 'vn' },
    ];

    useEffect(() => {
        const googleTranslateScriptId = 'google-translate-script';

        const initGoogleTranslate = () => {
            if (window.google && window.google.translate) {
                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                        includedLanguages: languages.map(l => l.code).join(','),
                        autoDisplay: false
                    },
                    'google_translate_element'
                );
            }
        };

        if (!document.getElementById(googleTranslateScriptId)) {
            const script = document.createElement('script');
            script.id = googleTranslateScriptId;
            script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.async = true;
            document.body.appendChild(script);
            window.googleTranslateElementInit = initGoogleTranslate;
        } else {
            initGoogleTranslate();
        }

        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Aggressively hide the Google Translate banner and original widget
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            iframe.goog-te-banner-frame { visibility: hidden !important; display: none !important; }
            body { top: 0px !important; }
            #google_translate_element { display: none !important; }
            .goog-te-gadget { display: none !important; }
            .goog-tooltip { display: none !important; }
            .goog-tooltip:hover { display: none !important; }
            .goog-text-highlight { background-color: transparent !important; border: none !important; box-shadow: none !important; }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    const changeLanguage = (lang) => {
        setSelectedLanguage(lang);
        setIsOpen(false);

        const select = document.querySelector('.goog-te-combo');
        if (select) {
            select.value = lang.code;
            select.dispatchEvent(new Event('change'));
        }
    };

    return (
        <div className="relative inline-block text-left z-50" ref={dropdownRef}>
            {/* Hidden Google Translate Element */}
            <div id="google_translate_element" style={{ display: 'none' }}></div>

            {/* Custom Dropdown Trigger */}
            <div>
                <button
                    type="button"
                    className="inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    id="menu-button"
                    aria-expanded="true"
                    aria-haspopup="true"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center">
                        <img
                            src={`https://flagcdn.com/w20/${selectedLanguage.flag}.png`}
                            alt={selectedLanguage.name}
                            className="mr-2 h-3 w-5 object-cover rounded-sm"
                        />
                        <span>{selectedLanguage.code.toUpperCase()}</span>
                    </div>
                    <ChevronDownIcon className="-mr-1 ml-2 h-4 w-4" aria-hidden="true" />
                </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none max-h-64 overflow-y-auto z-50"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                    tabIndex="-1"
                >
                    <div className="py-1" role="none">
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                className={`flex items-center w-full px-4 py-2 text-sm text-left ${selectedLanguage.code === lang.code ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`}
                                role="menuitem"
                                tabIndex="-1"
                                onClick={() => changeLanguage(lang)}
                            >
                                <img
                                    src={`https://flagcdn.com/w20/${lang.flag}.png`}
                                    alt={lang.name}
                                    className="mr-3 h-3 w-5 object-cover rounded-sm"
                                />
                                {lang.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoogleTranslate;
